import { useState, useEffect, useContext } from "react";
import { styled, useTheme } from "styled-components";
import CloseIcon from "@mui/icons-material/Close";
import { Toast as showToast } from "core/components/global/toast/toast";
import {
  getInvites,
  getGroups,
  updateInvite,
  sendInvite,
} from "modules/agentMySecurity/services/embeddingsService";
import { Invite } from "../../admin.type";
import { AuthContext } from "core/contexts/auth/auth";
import AddIcon from "@mui/icons-material/Add";
import { v4 as uuidv4 } from "uuid";

import AdminInvite from "./invite";
import AdvanceSearch from "core/components/global/advanceSearch/advanceSearch";
import {
  Button,
  IconButton,
  Autocomplete,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Checkbox,
  Paper,
  InputAdornment,
} from "@mui/material";
import {
  SimpleTableContainer,
  AdminTitle,
  AdminModal,
  AdminGroupsForm,
  AdminFormGroup,
  AdminModalBody,
  AdminModalButtonContainer,
  AdminUserGroupsChip,
  AdminAutocompleteForm,
  AdminModalInnerContainer,
  ValidatorText,
  AdminItemsRow,
} from "../../admin.styled";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SearchIcon from "@mui/icons-material/Search";
import NoResultsFound from "core/components/global/tables/noResults/noResultsFound";

// Define sort types
type SortColumn = any;
type SortDirection = "asc" | "desc" | null;

export default function AdminInvites() {
  const theme = useTheme();
  const { amsUser } = useContext(AuthContext);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [showDescValidator, setShowDescValidator] = useState<boolean>(false);
  const [showEmailValidator, setShowEmailValidator] = useState<boolean>(false);
  const [newInviteGroups, setNewInviteGroups] = useState<string[]>([]);
  const [description, setDescription] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [copyOfSearchValue, setCopyOfSearchValue] = useState("");
  const [groupSearchQuery, setGroupSearchQuery] = useState("");
  const [filteredGroups, setFilteredGroups] = useState<string[]>([]);

  // deletion state
  const [inviteToDelete, setInviteToDelete] = useState<Invite | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);


  // Column sorting state
  const [sortColumn, setSortColumn] = useState<SortColumn>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [filteredInvites, setFilteredInvites] = useState<Invite[] | null>(null);

  /**
   * Apply sorting whenever sort parameters change
   */
  useEffect(() => {
    if (!sortColumn || !sortDirection) {
      return;
    }

    const dataToSort = [...(filteredInvites === null ? invites : filteredInvites)];

    const sortedData: any = dataToSort.sort((a, b) => {
      const valueA = String(a[sortColumn]).toLowerCase();
      const valueB = String(b[sortColumn]).toLowerCase();

      if (valueA < valueB) {
        return sortDirection === "asc" ? -1 : 1;
      }

      if (valueA > valueB) {
        return sortDirection === "asc" ? 1 : -1;
      }

      return 0;
    });

    setFilteredInvites(filteredInvites === null ? sortedData : sortedData);
  }, [sortColumn, sortDirection]);

  /**
   * Handle column header click to sort
   */
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return null;
    }
    return sortDirection === "asc" ? (
      <ArrowUpwardIcon fontSize="small" className="ms-1" />
    ) : (
      <ArrowDownwardIcon fontSize="small" className="ms-1" />
    );
  };

  const handleFilterData = (newKeys, b, c) => {
    setFilteredInvites(newKeys);
    setSortColumn(null);
    setSortDirection(null);
  };

  const handleClearFilter = () => {
    setFilteredInvites(null);
  };

  useEffect(() => {
    async function fetchAdGroups() {
      try {
        const response = await getInvites();
        if (response) {
          setInvites(response);
        }
      } catch (error) {
        setInvites([]);
      }
    }

    fetchAdGroups();
  }, [groups]);

  useEffect(() => {
    async function fetchAdGroups() {
      try {
        const response = await getGroups();
        const groupNames = response.map((group) => group.name);
        setGroups(groupNames);
        setFilteredGroups(groupNames);
      } catch (error) {
        setGroups([]);
        setFilteredGroups([]);
      }
    }

    fetchAdGroups();
  }, []);
  
  // Filter groups based on search query
  useEffect(() => {
    if (groups.length > 0) {
      if (groupSearchQuery.trim() === "") {
        setFilteredGroups(groups);
      } else {
        const filtered = groups.filter(group => 
          group.toLowerCase().includes(groupSearchQuery.toLowerCase())
        );
        setFilteredGroups(filtered);
      }
    }
  }, [groupSearchQuery, groups]);

  const handleUpdateInvite = async (updatedInvite: Invite) => {
    // Update the user's groups
    if (updatedInvite) {
      // Post the updated user
      const response = await updateInvite(updatedInvite._id, updatedInvite);

      if (response !== null) {
        setOpen(false);
        showToast(response.message, "success");

        const updatedInvites = invites.map((invite) =>
          invite._id === updatedInvite._id
            ? {
                ...invite,
                ...updatedInvite,
              }
            : invite
        );

        setInvites(updatedInvites);
      }
    }
  };

  function handleUpdateNewGroup(event, group) {
    // if (!event.target.name) {
    //   setNewInviteGroups(group);
    // } else if (event.target.name === "email") {
    //   setEmail(event.target.value);
    // } else if (event.target.name === "description") {
    //   setDescription(event.target.value);
    // }
    const { name, value } = event.target;

  if (!name) {
    setNewInviteGroups(group);
  } else if (name === "email") {
    if (value.length <= 2000) { // Typical email max length
      setEmail(value);
    }
    // Optional: handle email validation error
  } else if (name === "description") {
    if (value.length <= 2000) {
      setDescription(value);
    }
  }

  }

  const handleSubmitNewGroup = async () => {
    if (email === "" || email.length === 1) {
      setShowEmailValidator(true);
    }
    if (description === "") {
      setShowDescValidator(true);
    }
    if (email !== "" && description !== "" && email.length > 1) {
      const response = await sendInvite({
        email: email,
        groups: newInviteGroups,
        description: description,
      });

      if (response) {
        // Create a temporary invite to add to the list
        const newInvite = {
          _id: uuidv4(),
          description: description,
          email: email,
          groupAssignments: newInviteGroups,
          active: true,
          used: false,
          verificationLink: "refresh",
          createdAt: "refresh",
          updatedAt: "refresh",
        };
        
        setOpen(false);
        showToast(response.message, "success");
        
        // Refresh the invites list by fetching the latest data
        try {
          const updatedInvites = await getInvites();
          if (updatedInvites) {
            setInvites(updatedInvites);
          } else {
            // Fallback to adding the new invite locally if API fetch fails
            setInvites([newInvite, ...invites]);
          }
        } catch (error) {
          // Fallback to adding the new invite locally if API fetch fails
          setInvites([newInvite, ...invites]);
        }
      }
    }
  };

  const handleRemoveNewGroup = (currentGroupName: string) => {
    setNewInviteGroups(
      newInviteGroups.filter((groupName) => groupName !== currentGroupName)
    );
  };
  
  // Handle group selection in the table
  const handleGroupSelection = (group: string) => {
    setNewInviteGroups(prevState => {
      const isSelected = prevState.includes(group);
      
      if (isSelected) {
        // Remove group if already selected
        return prevState.filter(g => g !== group);
      } else {
        // Add group if not selected
        return [...prevState, group];
      }
    });
  };
  
  // Check if a group is selected
  const isGroupSelected = (group: string) => {
    return newInviteGroups.includes(group);
  };
  
  // Handle select all groups
  const handleSelectAllGroups = () => {
    setNewInviteGroups([...filteredGroups]);
  };
  
  // Handle clear all groups
  const handleClearAllGroups = () => {
    setNewInviteGroups([]);
  };

  const handleDeleteInvite = (inviteId: string) => {
    setInvites((prev) => prev.filter((inv) => inv._id !== inviteId));
    setFilteredInvites((prev) =>
      prev ? prev.filter((inv) => inv._id !== inviteId) : null
    );
  };
  
  return (
    <>
      <SimpleTableContainer elevation={1} columns={2}>
        {/* Data Search */}
        <div className="pb-3 d-flex justify-content-between align-items-center">
          <h5>User Invites</h5>
          <AdvanceSearch
            initialData={invites}
            setFilteredData={handleFilterData}
            clearFilteredData={handleClearFilter}
            setCopyOfSearchValue={setCopyOfSearchValue}
          />
        </div>

        {/* Collapsable Table */}
        <Table aria-label="collapsible table">
          {/* Headers */}
          <TableHead
            style={{ borderRadius: "5px 5px 0 0" }}
            className="outer-header-container"
          >
            <div className="row-items">
              <p 
                className="header-item d-flex align-items-center"
                onClick={() => handleSort('email')}
                style={{ cursor: 'pointer' }}
              >
                Email
                {getSortIcon('email')}
              </p>
              <p 
                className="header-item d-flex align-items-center"
                onClick={() => handleSort('description')}
                style={{ cursor: 'pointer' }}
              >
                Description
                {getSortIcon('description')}
              </p>
            </div>
            <div className="row-buttons">
              <IconButton
                aria-label="send prompt"
                onClick={() => setOpen(true)}
                edge="end"
                className="me-1 icon-button"
              >
                <AddIcon />
              </IconButton>
            </div>
          </TableHead>

          {/* Body */}
          <TableBody
            style={{ borderRadius: "0 0 5px 5px" }}
            className="outer-row-container"
          >
            {/* Iterrate the invites */}
            {(filteredInvites === null ? invites : filteredInvites).length > 0 ? (
              filteredInvites === null ? (
                <>
                  {invites.map((invite) => (
                    // Row items
                    <AdminInvite
                      key={`row-${invite._id}`}
                      invite={invite}
                      amsUser={amsUser}
                      availableGroups={groups}
                      handleUpdateInvite={handleUpdateInvite}
                      onDelete={handleDeleteInvite}
                    />
                  ))}
                </>
              ) : (
                <>
                  {filteredInvites.map((invite) => (
                    // Row items
                    <AdminInvite
                      key={`row-${invite._id}`}
                      invite={invite}
                      amsUser={amsUser}
                      availableGroups={groups}
                      handleUpdateInvite={handleUpdateInvite}
                      onDelete={handleDeleteInvite}
                    />
                  ))}
                </>
              )
            ) : (
              <NoResultsFound
                searchTerm={filteredInvites !== null ? copyOfSearchValue : ""}
              />
            )}
          </TableBody>
        </Table>
      </SimpleTableContainer>

      {/* Modal */}
      <AdminModal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <AdminModalInnerContainer elevation={2}>
          {/* Header */}
          <AdminTitle>
            <div className="d-flex">
              <p>Invite Someone</p>
            </div>
          </AdminTitle>

          {/* Body */}
          <AdminModalBody>
            <AdminGroupsForm>
              {/* Name field */}
              <AdminFormGroup>
                <label>Group Name</label>
                <input
                  onChange={(event) => handleUpdateNewGroup(event, "")}
                  placeholder="Comma Separated Email Addresses"
                  name="email"
                  aria-placeholder="Enter the email"
                  style={{ color: theme.colors.white }}
                  maxLength={2000}
                />
                {showEmailValidator && (
                  <ValidatorText>
                    <p>A group can't be empty</p>
                    <p>A group must have 2 or more characters</p>
                  </ValidatorText>
                )}
              </AdminFormGroup>

              {/* Description Field */}
              <AdminFormGroup>
                <label>Description</label>
                <textarea
                  onChange={(event) => handleUpdateNewGroup(event, "")}
                  placeholder="Description"
                  name="description"
                  aria-placeholder="Describe the group"
                  maxLength={2000}
                />
                {showDescValidator && (
                  <ValidatorText>
                    <p>A description can't be empty</p>
                  </ValidatorText>
                )}
              </AdminFormGroup>

              {/* AD Groups Field */}
              <AdminFormGroup>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={{ marginBottom: 0, marginRight: '8px' }}>Groups</label>
                  <span style={{ 
                    backgroundColor: theme.colors.sidebarcolor, 
                    color: theme.colors.white, 
                    borderRadius: '16px', 
                    padding: '2px 8px', 
                    fontSize: theme.font.small, 
                    fontWeight: 500,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '24px',
                    height: '20px'
                  }}>
                    {newInviteGroups.length}
                  </span>
                </div>
                
                {/* Group selection table */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    {/* Search box */}
                    <TextField
                      placeholder="Search groups..."
                      variant="outlined"
                      size="small"
                      value={groupSearchQuery}
                      onChange={(e) => setGroupSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" className="ms-2">
                            <SearchIcon style={{ color: theme.colors.white }}/>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        flexGrow: 1,
                        margin: 0,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: theme.colors.light,
                          borderRadius: '4px',
                          margin: 0,
                          padding: 0,
                          height: '37.5px',
                          '& fieldset': {
                            borderColor: 'transparent',
                            borderWidth: 0,
                          },
                          '&:hover fieldset': {
                            borderColor: 'transparent',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'transparent',
                            borderWidth: 0,
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: theme.colors.white,
                          fontSize: theme.font.h4,
                          padding: '5px 12px',
                          height: '24px',
                          border: "none"
                        },
                        '& .MuiInputBase-input:active': {
                          border: "none"
                        },
                      }}
                    />
                    
                    {/* Bulk action buttons */}
                    <div className="d-flex gap-2 ms-2">
                      <Button 
                        variant="contained"
                        onClick={handleSelectAllGroups}
                        sx={{
                          backgroundColor: theme.colors.sidebarcolor,
                          color: theme.colors.white,
                          textTransform: 'none',
                          fontSize: theme.font.h4,
                          padding: '4px 12px',
                          minWidth: '80px',
                          '&:hover': {
                            backgroundColor: theme.name === 'light-theme' 
                              ? theme.colors.loginBackgroundHover 
                              : theme.colors.sidebarLightHover,
                          }
                        }}
                      >
                        Select All
                      </Button>
                      <Button 
                        variant="contained"
                        onClick={handleClearAllGroups}
                        sx={{
                          backgroundColor: theme.colors.sidebarcolor,
                          color: theme.colors.white,
                          textTransform: 'none',
                          fontSize: theme.font.h4,
                          padding: '4px 12px',
                          minWidth: '80px',
                          '&:hover': {
                            backgroundColor: theme.name === 'light-theme' 
                              ? theme.colors.loginBackgroundHover 
                              : theme.colors.sidebarLightHover,
                          }
                        }}
                      >
                        Clear All
                      </Button>
                    </div>
                  </div>
                  
                  {/* Group table */}
                  <TableContainer
                    component={Paper}
                    style={{ 
                      maxHeight: '300px', 
                      overflowY: 'auto',
                      borderRadius: theme.borders.borderRadius,
                      boxShadow: 'none',
                      backgroundColor: theme.name === 'light-theme' ? theme.colors.white : theme.colors.tableBackground
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell 
                            padding="checkbox"
                            style={{
                              backgroundColor: theme.name === 'light-theme' ? '#F5F5F5' : theme.colors.mediumGrey,
                              borderBottom: `1px solid ${theme.colors.lightGrey}`
                            }}
                          >
                            <Checkbox
                              indeterminate={
                                newInviteGroups.length > 0 &&
                                newInviteGroups.length < filteredGroups.length
                              }
                              checked={
                                filteredGroups.length > 0 &&
                                newInviteGroups.length === filteredGroups.length
                              }
                              onChange={e => {
                                if (e.target.checked) {
                                  handleSelectAllGroups();
                                } else {
                                  handleClearAllGroups();
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell
                            style={{
                              backgroundColor: theme.name === 'light-theme' ? '#F5F5F5' : theme.colors.mediumGrey,
                              borderBottom: `1px solid ${theme.colors.lightGrey}`,
                              fontSize: theme.font.h4,
                              fontWeight: 600,
                              color: theme.name === 'light-theme' ? theme.colors.textColor : theme.colors.white,
                              padding: '12px 16px'
                            }}
                          >
                            Group Name
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredGroups.length > 0 ? (
                          filteredGroups.map((group) => (
                            <TableRow 
                              key={group}
                              selected={isGroupSelected(group)}
                              onClick={() => handleGroupSelection(group)}
                              hover
                              style={{ 
                                cursor: 'pointer',
                                backgroundColor: isGroupSelected(group) 
                                  ? theme.colors.historyItemselect 
                                  : theme.name === 'light-theme' ? theme.colors.white : theme.colors.tableBackground,
                                borderBottom: `1px solid ${theme.colors.lightGrey}`
                              }}
                              sx={{
                                '&:hover': {
                                  backgroundColor: theme.colors.historyItemhvr,
                                },
                              }}
                            >
                              <TableCell 
                                padding="checkbox"
                                style={{
                                  padding: '10px 0 10px 16px',
                                  borderBottom: `1px solid ${theme.colors.lightGrey}`
                                }}
                              >
                                <Checkbox 
                                  checked={isGroupSelected(group)}
                                  style={{
                                    color: theme.colors.sidebarcolor
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                style={{
                                  fontSize: theme.font.h4,
                                  color: theme.colors.textColor,
                                  padding: '12px 16px',
                                  borderBottom: `1px solid ${theme.colors.lightGrey}`
                                }}
                              >
                                {group}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell 
                              colSpan={2} 
                              align="center"
                              style={{
                                padding: '16px',
                                color: theme.colors.textDisabledColor,
                                fontSize: theme.font.h4,
                                borderBottom: 'none'
                              }}
                            >
                              {groupSearchQuery ? 'No matching groups found' : 'No groups available'}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </AdminFormGroup>
            </AdminGroupsForm>
          </AdminModalBody>
          <AdminModalButtonContainer>
            <Button variant="contained" onClick={() => handleSubmitNewGroup()}>
              Create Invite
            </Button>
          </AdminModalButtonContainer>
        </AdminModalInnerContainer>
      </AdminModal>
    </>
  );
}
